import React, { useRef } from "react"
import styled from "~ui/styled"
import { useLocation, useNavigate, useOutlet } from "react-router-dom"
import { CenterFlex } from "~components/El"
import PcDetailWatermark from "./PcDetailWatermark"
import PcPresentationShell from "./PcPresentationShell"
import {
  getMePresentationTitle,
  getPcPresentationForPath,
  getPcPresentationOptions,
  shouldShowMePresentationHeader,
} from "./pc-presentation"
import { isMeTabContext } from "~ui/app-shell"
import { routeNameFromPathname } from "./route-map"
import { useRoute } from "./navigation"

const BaseLayer = styled.div<{ $inline?: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
  ${({ $inline }) =>
    $inline
      ? `
    grid-area: 1 / 1;
    position: relative;
  `
      : `
    position: absolute;
    inset: 0;
  `}
`

const CenterStage = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
`

const RouteLayer = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`

const PresentationHost = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--pc-detail-empty-bg);
`

const EmptyDetail = styled(CenterFlex)`
  flex: 1;
  flex-direction: column;
  user-select: none;
  background: var(--pc-detail-empty-bg);
`

/** 跨 PopupProvider 重挂载保留的底层详情（如聊天室） */
let frozenDetailBaseOutlet: React.ReactNode = null

const PRESENTATION_TITLES: Record<string, string> = {
  AddContact: "添加好友",
  AddGroupChat: "创建群聊",
  AddChannel: "创建频道",
  ChannelList: "频道",
  GroupList: "群聊",
  FriendList: "新的朋友",
  GroupChatManagement: "群聊管理",
  GroupChatInformation: "群聊信息",
  ChannelChatManagement: "频道管理",
  ChannelChatInformation: "频道信息",
  PersonalCard: "个人信息",
}

/**
 * PC 详情区出口：presentation 路由以浮层展示，底层保留上一次详情内容（如聊天室）。
 */
export default function PcDetailOutlet() {
  const location = useLocation()
  const navigate = useNavigate()
  const route = useRoute()
  const presentation = getPcPresentationForPath(location.pathname)
  const outlet = useOutlet()
  const baseOutletRef = useRef<React.ReactNode>(null)

  if (!presentation && outlet != null) {
    baseOutletRef.current = outlet
    if (location.pathname === "/room") {
      frozenDetailBaseOutlet = outlet
    }
  }

  const routeName = routeNameFromPathname(location.pathname)
  const params = route.params as Record<string, unknown> | undefined
  const title =
    getMePresentationTitle(routeName, params) ??
    (routeName ? PRESENTATION_TITLES[routeName] : undefined)
  const presentationOptions = getPcPresentationOptions(routeName)
  const showShellHeader =
    presentation === "center"
      ? shouldShowMePresentationHeader(routeName)
      : presentation === "drawer"

  if (presentation) {
    const chatId = new URLSearchParams(location.search).get("chat_id")
    const watermarkBase = (
      <EmptyDetail>
        <PcDetailWatermark />
      </EmptyDetail>
    )
    const frozenBase =
      presentation === "drawer" || chatId
        ? baseOutletRef.current ?? frozenDetailBaseOutlet
        : null
    const base = presentation === "center" ? watermarkBase : frozenBase ?? watermarkBase

    const shell = (
      <PcPresentationShell
        type={presentation}
        title={title}
        showHeader={showShellHeader}
        centerHeight={presentationOptions?.height}
        centerLayout={presentation === "center" ? "inline" : "overlay"}
        onClose={() => {
          if (isMeTabContext(location.pathname)) {
            navigate("/me", { replace: true })
            return
          }
          navigate(-1)
        }}
      >
        {outlet}
      </PcPresentationShell>
    )

    if (presentation === "center") {
      return (
        <PresentationHost>
          <CenterStage>
            <BaseLayer $inline aria-hidden>
              {watermarkBase}
            </BaseLayer>
            {shell}
          </CenterStage>
        </PresentationHost>
      )
    }

    return (
      <PresentationHost>
        <BaseLayer aria-hidden>{base}</BaseLayer>
        {shell}
      </PresentationHost>
    )
  }

  return <RouteLayer>{outlet}</RouteLayer>
}
